/**
 * Cache Manager
 * 
 * Orchestrates multiple cache layers and provides unified interface.
 * Handles different data types with appropriate caching strategies.
 */

import { EnhancedCache, CacheLayer, CacheStats } from './EnhancedCache';
import { IslandData, FishingSpotData } from '../game/types';
import { 
  ChunkDataSchema, 
  PlayerDataSchema, 
  MarketDataSchema, 
  UIStateSchema 
} from './cache/schemas';
import ImageCacheManager from './ImageCacheManager';
import ImagePreloader from './ImagePreloader';

// Re-export CacheStats for external use
export type { CacheStats };

// Data types for different cache categories
export interface ChunkData {
  islands?: IslandData[];
  fishingSpots?: FishingSpotData[];
}

export interface PlayerData {
  inventory?: Array<{ itemId: string; quantity: number }>;
  skills?: Record<string, any>;
  stats?: { pieces: number; level: number; xp: number; infamy: number };
  equipment?: Record<string, string>;
  hotbar?: Record<number, string>;
}

export interface MarketData {
  shopItems?: Array<{ id: string; price: number; stock: number }>;
  prices?: Record<string, number>;
  lastUpdated?: number;
}

export interface CacheConfig {
  chunks: { layer: CacheLayer; maxSize: number; ttl: number; staleThresholdMs?: number | null };
  player: { layer: CacheLayer; maxSize: number; ttl: number; staleThresholdMs?: number | null };
  market: { layer: CacheLayer; maxSize: number; ttl: number; staleThresholdMs?: number | null };
  ui: { layer: CacheLayer; maxSize: number; ttl: number; staleThresholdMs?: number | null };
}

export class CacheManager {
  private static instance: CacheManager;
  
  // Cache instances for different data types
  private chunkCache!: EnhancedCache<ChunkData>;
  private playerCache!: EnhancedCache<PlayerData>;
  private marketCache!: EnhancedCache<MarketData>;
  private uiCache!: EnhancedCache<any>;
  
  // Configuration
  private config: CacheConfig = {
    chunks: { layer: 'memory', maxSize: 200, ttl: 300000, staleThresholdMs: 300000 }, // 5 minutes
    player: { layer: 'session', maxSize: 50, ttl: 1800000, staleThresholdMs: null }, // 30 minutes
    market: { layer: 'persistent', maxSize: 100, ttl: 3600000, staleThresholdMs: null }, // 1 hour
    ui: { layer: 'session', maxSize: 30, ttl: 604800000, staleThresholdMs: null }, // 1 week
  };

  private constructor() {
    this.checkAndClearStaleCache();
    this.initializeCaches();
    this.registerUnloadFlush();
  }

  /**
   * Check cache version and clear if stale
   * This prevents issues when code changes but old cache remains
   */
  private checkAndClearStaleCache(): void {
    const CACHE_VERSION = '1.1.1'; // Increment this when making breaking changes
    const CACHE_VERSION_KEY = 'grandline_cache_version';
    
    try {
      const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
      
      if (storedVersion !== CACHE_VERSION) {
        console.log(`🗑️ Cache version mismatch (stored: ${storedVersion}, current: ${CACHE_VERSION}) - clearing all caches`);
        
        // Clear all localStorage keys related to our game
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('grandline_')) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log(`  ✓ Cleared: ${key}`);
        });
        
        // Clear sessionStorage as well
        const sessionKeysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('grandline_')) {
            sessionKeysToRemove.push(key);
          }
        }
        
        sessionKeysToRemove.forEach(key => {
          sessionStorage.removeItem(key);
          console.log(`  ✓ Cleared: ${key}`);
        });
        
        // Set new version
        localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
        console.log(`✅ Cache cleared and version updated to ${CACHE_VERSION}`);
      } else {
        console.log(`✓ Cache version ${CACHE_VERSION} is current`);
      }
    } catch (error) {
      console.error('Failed to check cache version:', error);
      // If we can't check version, proceed anyway (don't break the app)
    }
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  // Initialize all cache layers
  private initializeCaches(): void {
    this.chunkCache = new EnhancedCache('memory', {
      maxSize: this.config.chunks.maxSize,
      defaultTtl: this.config.chunks.ttl,
      storageKey: 'grandline_chunks',
      validator: ChunkDataSchema,
      strictValidation: false, // Log errors but don't throw
      staleThresholdMs: this.config.chunks.staleThresholdMs,
    });

    this.playerCache = new EnhancedCache('session', {
      maxSize: this.config.player.maxSize,
      defaultTtl: this.config.player.ttl,
      enablePersistence: true,
      storageKey: 'grandline_player',
      validator: PlayerDataSchema,
      strictValidation: true, // Player data must be valid
      staleThresholdMs: this.config.player.staleThresholdMs,
    });

    this.marketCache = new EnhancedCache('persistent', {
      maxSize: this.config.market.maxSize,
      defaultTtl: this.config.market.ttl,
      enablePersistence: true,
      storageKey: 'grandline_market',
      validator: MarketDataSchema,
      strictValidation: false,
      staleThresholdMs: this.config.market.staleThresholdMs,
    });

    this.uiCache = new EnhancedCache('session', {
      maxSize: this.config.ui.maxSize,
      defaultTtl: this.config.ui.ttl,
      enablePersistence: true,
      storageKey: 'grandline_ui',
      validator: UIStateSchema,
      strictValidation: false,
      staleThresholdMs: this.config.ui.staleThresholdMs,
    });
  }

  // Chunk data operations
  public getChunk(chunkId: string): ChunkData | undefined {
    return this.chunkCache.get(chunkId);
  }

  public setChunk(chunkId: string, data: ChunkData, options?: {
    serverTimestamp?: number;
    version?: number;
    force?: boolean;
  }): boolean {
    return this.chunkCache.set(chunkId, data, {
      serverTimestamp: options?.serverTimestamp,
      version: options?.version,
      force: options?.force,
    });
  }

  public hasChunk(chunkId: string): boolean {
    return this.chunkCache.has(chunkId);
  }

  public clearChunks(): void {
    this.chunkCache.clear();
  }

  public deleteChunk(chunkId: string): boolean {
    return this.chunkCache.delete(chunkId);
  }

  // Player data operations
  public getPlayerData(key: string): PlayerData | undefined {
    return this.playerCache.get(key);
  }

  public setPlayerData(key: string, data: PlayerData, options?: {
    serverTimestamp?: number;
    version?: number;
    force?: boolean;
  }): boolean {
    return this.playerCache.set(key, data, {
      serverTimestamp: options?.serverTimestamp,
      version: options?.version,
      force: options?.force,
    });
  }

  public hasPlayerData(key: string): boolean {
    return this.playerCache.has(key);
  }

  public clearPlayerData(): void {
    this.playerCache.clear();
  }

  public deletePlayerData(key: string): boolean {
    return this.playerCache.delete(key);
  }

  // Market data operations
  public getMarketData(key: string): MarketData | undefined {
    return this.marketCache.get(key);
  }

  public setMarketData(key: string, data: MarketData, options?: {
    serverTimestamp?: number;
    version?: number;
    force?: boolean;
  }): boolean {
    return this.marketCache.set(key, data, {
      serverTimestamp: options?.serverTimestamp,
      version: options?.version,
      force: options?.force,
    });
  }

  public hasMarketData(key: string): boolean {
    return this.marketCache.has(key);
  }

  public clearMarketData(): void {
    this.marketCache.clear();
  }

  public deleteMarketData(key: string): boolean {
    return this.marketCache.delete(key);
  }

  // UI state operations
  public getUIState<T>(key: string): T | undefined {
    return this.uiCache.get(key);
  }

  public setUIState<T>(key: string, state: T): void {
    this.uiCache.set(key, state);
  }

  public hasUIState(key: string): boolean {
    return this.uiCache.has(key);
  }

  public clearUIState(): void {
    this.uiCache.clear();
  }

  public deleteUIState(key: string): boolean {
    return this.uiCache.delete(key);
  }

  // Utility methods

  public async getCombinedStats(): Promise<Record<string, CacheStats | any>> {
    const imageStats = await ImageCacheManager.getStats();
    
    return {
      chunks: this.chunkCache.getStats(),
      player: this.playerCache.getStats(),
      market: this.marketCache.getStats(),
      ui: this.uiCache.getStats(),
      images: imageStats,
    };
  }

  public async clearAll(): Promise<void> {
    this.chunkCache.clear();
    this.playerCache.clear();
    this.marketCache.clear();
    this.uiCache.clear();
    await ImageCacheManager.clear();
  }

  /**
   * Force clear all browser storage (localStorage + sessionStorage + IndexedDB)
   * Useful for debugging or when cache gets corrupted
   */
  public static async forceResetCache(): Promise<void> {
    console.log('🗑️ Force resetting all cache...');
    
    // Clear all grandline-related localStorage
    const localKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('grandline_')) {
        localKeys.push(key);
      }
    }
    localKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`  ✓ Cleared localStorage: ${key}`);
    });
    
    // Clear all grandline-related sessionStorage
    const sessionKeys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('grandline_')) {
        sessionKeys.push(key);
      }
    }
    sessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log(`  ✓ Cleared sessionStorage: ${key}`);
    });
    
    // Clear image cache
    await ImageCacheManager.clear();
    console.log('  ✓ Cleared image cache');
    
    console.log('✅ Cache force reset complete. Reload the page to reinitialize.');
  }

  /**
   * Get current cache version
   */
  public static getCacheVersion(): string {
    return localStorage.getItem('grandline_cache_version') || 'unknown';
  }

  public async cleanupAll(): Promise<number> {
    const imageCleanup = await ImageCacheManager.cleanupExpired();
    return (
      this.chunkCache.cleanup() +
      this.playerCache.cleanup() +
      this.marketCache.cleanup() +
      this.uiCache.cleanup() +
      imageCleanup
    );
  }

  public preloadChunks(chunkIds: string[]): Promise<void[]> {
    const promises = chunkIds.map(async (chunkId) => {
      if (!this.hasChunk(chunkId)) {
        try {
          const [x, y] = chunkId.split('_').map(Number);
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          
          // Check if we have version info
          const versionInfo = this.chunkCache.getVersionInfo?.(chunkId);
          const headers: Record<string, string> = {};
          
          if (versionInfo?.etag) {
            headers['If-None-Match'] = versionInfo.etag;
          }
          if (versionInfo?.timestamp) {
            headers['If-Modified-Since'] = new Date(versionInfo.timestamp).toUTCString();
          }
          
          const response = await fetch(`${apiUrl}/api/world/chunk/${x}/${y}`, {
            headers,
          });
          
          // Handle 304 Not Modified
          if (response.status === 304) {
            console.log(`Chunk ${chunkId} not modified, using cache`);
            return;
          }
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const data = await response.json();
          const serverTimestamp = response.headers.get('X-Timestamp') 
            ? parseInt(response.headers.get('X-Timestamp')!)
            : Date.now();
          
          this.setChunk(chunkId, data, {
            serverTimestamp,
          });
        } catch (error) {
          console.warn(`Failed to preload chunk ${chunkId}:`, error);
        }
      }
    });
    
    return Promise.all(promises);
  }

  public invalidatePlayerData(characterId: string): void {
    // Clear all player-specific data
    const keys = this.playerCache.keys().filter(key => 
      key.includes(characterId) || key.startsWith('player_')
    );
    
    keys.forEach(key => this.playerCache.delete(key));
  }

  public updateConfig(newConfig: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Reinitialize affected caches with new config
    if (newConfig.chunks) {
      this.chunkCache.destroy();
      this.chunkCache = new EnhancedCache('memory', {
        maxSize: this.config.chunks.maxSize,
        defaultTtl: this.config.chunks.ttl,
        storageKey: 'grandline_chunks',
      });
    }
    
    // Similar updates for other cache types...
  }

  public destroy(): void {
    this.flushAll();
    this.chunkCache.destroy();
    this.playerCache.destroy();
    this.marketCache.destroy();
    this.uiCache.destroy();
    ImageCacheManager.destroy();
  }

  public flushAll(): void {
    this.chunkCache.flush();
    this.playerCache.flush();
    this.marketCache.flush();
    this.uiCache.flush();
  }

  private registerUnloadFlush(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('beforeunload', () => {
      this.flushAll();
    });
  }

  // Debug methods
  public async exportDebugInfo(): Promise<any> {
    const stats = await this.getCombinedStats();
    return {
      config: this.config,
      stats,
      keys: {
        chunks: this.chunkCache.keys(),
        player: this.playerCache.keys(),
        market: this.marketCache.keys(),
        ui: this.uiCache.keys(),
      },
      validation: {
        chunksEnabled: !!this.chunkCache['validator'],
        playerEnabled: !!this.playerCache['validator'],
        marketEnabled: !!this.marketCache['validator'],
        uiEnabled: !!this.uiCache['validator'],
      },
    };
  }
  
  /**
   * Preload inventory images
   */
  public async preloadInventoryImages(inventory: Array<{ itemId: string; icon?: string }>): Promise<void> {
    await ImagePreloader.preloadInventoryItems(inventory);
  }
  
  /**
   * Preload shop images
   */
  public async preloadShopImages(shopItems: Array<{ icon?: string }>): Promise<void> {
    await ImagePreloader.preloadShopItems(shopItems);
  }
  
  /**
   * Get image cache manager
   */
  public getImageCache(): typeof ImageCacheManager {
    return ImageCacheManager;
  }
  
  /**
   * Get image preloader
   */
  public getImagePreloader(): typeof ImagePreloader {
    return ImagePreloader;
  }
  
  /**
   * Check if cached data is newer than provided timestamp
   */
  public isDataNewerThan(type: 'chunks' | 'player' | 'market' | 'ui', key: string, timestamp: number): boolean {
    const cache = (this as any)[`${type}Cache`];
    return cache.isNewerThan?.(key, timestamp) || false;
  }
  
  /**
   * Get version info for cached data
   */
  public getVersionInfo(type: 'chunks' | 'player' | 'market' | 'ui', key: string) {
    const cache = (this as any)[`${type}Cache`];
    return cache.getVersionInfo?.(key);
  }
}

// Export singleton instance as default
const cacheManagerInstance = CacheManager.getInstance();
export default cacheManagerInstance;
